/* eslint-disable @typescript-eslint/no-use-before-define */

import { z } from "zod"
import { useCallback, useState } from "preact/hooks"
import { FORM_ERROR } from "final-form"
import { Field, Form, FormSpy } from "react-final-form"
import css from "../style.module.scss"
import { $user } from "../hooks/user"
import { navigate, replace } from "../hooks/navigation"
import { useSupport } from "../hooks/support"
import { t } from "../hooks/translations"
import { OffersScreen } from "./OffersScreen"
import { SocialAuth } from "../components/SocialAuth"

const signInSchema = z.object({
  email: z.string().email().trim(),
  password: z.string().min(6).trim(),
})

type SignInValues = z.infer<typeof signInSchema>

export const SignInScreen = function SignInScreen() {
  return (
    <div className={css["flex-container"]}>
      <div className={css["offers-screen"]}>
        <OffersScreen showContinueButton={false} />
      </div>

      <div className={css.splitter} />

      <div className={css["signup-screen"]}>
        <SignIn />
      </div>
    </div>
  )
}

const SignIn = function SignIn() {
  const { auth_providers: authProviders } = useSupport()
  const hasSocialAuth = Boolean(authProviders?.apple?.enabled || authProviders?.google?.enabled)
  const [initialValues] = useState<SignInValues>(() => ({
    email: $user.get().email,
  }))

  const onSubmit = useCallback(async (values: SignInValues) => {
    const result = signInSchema.safeParse(values)
    if (!result.success) {
      return result.error.flatten().fieldErrors
    }

    try {
      const response = await fetch("/session", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email: result.data.email,
          password: result.data.password,
        }),
      })

      const json = await response.json()
      if (json.success) {
        if (json.user != null) {
          $user.setKey("premium", json.user.premium)
          $user.setKey("forcedCurrency", json.user.forcedCurrency)
        }

        $user.setKey("signedIn", true)

        if (json.user?.premium) {
          window.location.reload()
        } else {
          replace("Payment")
        }

        return {}
      }

      if (json.errors != null) {
        return { ...json, [FORM_ERROR]: json.errors.join(" ") }
      }

      return json
    } catch (error) {
      return { [FORM_ERROR]: String(error) }
    }
  }, [])

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, pristine, submitError, handleSubmit }) => (
        <form className={css.signup} onSubmit={handleSubmit}>
          <FormSpy
            subscription={{ values: true }}
            onChange={(state) => {
              $user.setKey("email", state.values.email)
            }}
          />
          <h3 className={`${css.title} ${css["title--login"]}`}>{t("log_in")}</h3>
          {hasSocialAuth && <SocialAuth authProviders={authProviders} mode="signIn" />}
          {hasSocialAuth && <p className={`${css.or} ${css["or--login"]}`}>{t("or")}</p>}

          <fieldset className={css["signup-fieldset"]}>
            <Field<string>
              name="email"
              render={({ input, meta }) => (
                <label htmlFor="signin-email">
                  {t("email")}
                  <input
                    {...input}
                    className={css["signup-input"]}
                    type="email"
                    autoComplete="email"
                    placeholder="Email address"
                    id="signin-email"
                    disabled={submitting}
                  />
                  {(meta.error || meta.submitError) && meta.touched && (
                    <div className={css["signup-field-error"]}>
                      {meta.error || meta.submitError}
                    </div>
                  )}
                </label>
              )}
            />
            <Field<string>
              name="password"
              render={({ input, meta }) => (
                <label htmlFor="signin-new-password">
                  {t("password")}
                  <input
                    {...input}
                    className={css["signup-input"]}
                    type="password"
                    autoComplete="password"
                    placeholder="Password"
                    id="signin-new-password"
                    disabled={submitting}
                  />
                  {(meta.error || meta.submitError) && meta.touched && (
                    <div className={css["signup-field-error"]}>
                      {meta.error || meta.submitError}
                    </div>
                  )}

                  <a
                    className={css["signup-field-note"]}
                    href="/password/new"
                    onClick={(ev) => {
                      ev.preventDefault()
                      navigate("ForgotPassword")
                    }}
                  >
                    {t("forgot_password")}
                  </a>
                </label>
              )}
            />
          </fieldset>

          {submitError && <div className={css["submit-error"]}>{submitError}</div>}

          <button className={css.button} type="submit" disabled={submitting || pristine}>
            {t("log_in")}
          </button>

          <p className={css["terms-text"]}>
            {t("agree_to_terms")}{" "}
            <a
              className={css["terms-text-link"]}
              href="/pages/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Services
            </a>
          </p>

          <p className={css["join-offer"]}>
            {t("dont_have_an_account")}{" "}
            <a
              className={css["join-offer-link"]}
              href="/sign_in"
              onClick={(ev) => {
                ev.preventDefault()
                replace("SignUp")
              }}
            >
              {t("join_now")}
            </a>
          </p>
        </form>
      )}
    />
  )
}
